import { create } from 'zustand';
import { DataTelemetryData, DataTelemetryKPI } from '../models/telemetry';
import { telemetryApi } from '../api/telemetry.api';
import { timestampFromMs, timestampMs } from '@bufbuild/protobuf/wkt';
import { AggregationType, DataTelemetry } from '../gen/proto/api/gateway/v1/gateway_pb';
import { getOptimalBucketSize } from '../utils/utils';

type DataTelemetryStoreEntity = { [key: number]: DataTelemetry };

interface TelemetryStore {
  entities: DataTelemetryStoreEntity;
  totalEntities: number;
  entitiesPagination: DataTelemetryStoreEntity;
  totalEntitiesPagination: number;
  topic: string;
  kpis: DataTelemetryKPI;
  getTopics: (uuid: string) => Promise<string | null>;
  getActualTelemetryData: (uuid: string) => Promise<DataTelemetryData | null>;
  getTelemetryDataByTimestamp: (uuid: string, startDate: string, endDate: string) => Promise<DataTelemetry[]>;
  getDeviceKPIs: (uuid: string, startDate: string, endDate: string) => Promise<DataTelemetryKPI | null>;
  getTelemetryDataByPaginationAndTimestamp: (
    uuid: string,
    pageIndex: number,
    pageSize: number,
    startDate: string,
    endDate: string,
  ) => Promise<DataTelemetry[]>;
}

export const useTelemetryStore = create<TelemetryStore>()((set, get) => ({
  entities: {} as DataTelemetryStoreEntity,
  topic: '',
  totalEntities: 0,
  entitiesPagination: {} as DataTelemetryStoreEntity,
  totalEntitiesPagination: 0,
  kpis: {} as DataTelemetryKPI,
  getTopics: async (uuid: string) => {
    const { topic } = get();

    if (topic !== '') {
      return topic;
    }

    const list = await telemetryApi.getDeviceTopicList(uuid);
    if (!list || list.length === 0) return null;

    const firstTopic = list[0];

    // Guardem al store per reutilitzar
    set({ topic: firstTopic });

    return firstTopic;
  },
  getActualTelemetryData: async (uuid: string) => {
    try {
      const topic = await get().getTopics(uuid);
      if (!topic) return null;

      const response = await telemetryApi.getDeviceDataTelemetry(
        uuid,
        topic,
        timestampFromMs(0),
        timestampFromMs(0),
        0,
        1,
        AggregationType.AGGREGATION_UNSPECIFIED,
        BigInt(0),
      );
      if (!response.dataTelemetry || response.dataTelemetry.length === 0) return null;

      const first = response.dataTelemetry[0];
      if (!first.data || !first.timestamp) return null;

      const timestamp = timestampMs(response.dataTelemetry[0].timestamp!);

      set((state) => {
        const entities = {
          ...state.entities,
          [timestamp]: first,
        };

        return {
          entities,
          totalEntities: Object.keys(entities).length,
        };
      });
      return response.dataTelemetry[0].data as DataTelemetryData;
    } catch (error) {
      return null;
    }
  },
  getTelemetryDataByTimestamp: async (uuid: string, startDate: string, endDate: string) => {
    try {
      const topic = await get().getTopics(uuid);
      if (!topic) return [];

      const startTimeMs = new Date(startDate).getTime();
      const endTimeMs = new Date(endDate).getTime();

      const intervalSec = getOptimalBucketSize(startDate, endDate);

      const totalResponse = await telemetryApi.getDeviceDataTelemetry(
        uuid,
        topic,
        timestampFromMs(startTimeMs),
        timestampFromMs(endTimeMs),
        0,
        1,
        AggregationType.FIRST,
        BigInt(intervalSec),
      );
      if (!totalResponse.dataTelemetry || totalResponse.dataTelemetry.length === 0) return [];

      const totalCount = totalResponse.metadata?.totalCount ?? 100;

      const response = await telemetryApi.getDeviceDataTelemetry(
        uuid,
        topic,
        timestampFromMs(startTimeMs),
        timestampFromMs(endTimeMs),
        0,
        totalCount,
        AggregationType.FIRST,
        BigInt(intervalSec),
      );

      if (!response.dataTelemetry || response.dataTelemetry.length === 0) return [];

      return response.dataTelemetry.filter((item) => item.data && item.timestamp);
    } catch (error) {
      return [];
    }
  },
  getDeviceKPIs: async (uuid: string, startDate: string, endDate: string) => {
    try {
      const topic = await get().getTopics(uuid);
      if (!topic) return null;

      const startTimeMs = new Date(startDate).getTime();
      const endTimeMs = new Date(endDate).getTime();

      const response = await telemetryApi.getDeviceKPI(
        uuid,
        topic,
        timestampFromMs(startTimeMs),
        timestampFromMs(endTimeMs),
      );

      const kpis = {
        totalAlerts: Number(response.totalAlerts),
        totalEvents: Number(response.totalEvents),
        mostUsedCommand: response.mostUsedCommand,
      } as DataTelemetryKPI;

      set(() => ({
        kpis: kpis,
      }));

      return kpis;
    } catch (error) {
      return null;
    }
  },
  getTelemetryDataByPaginationAndTimestamp: async (
    uuid: string,
    pageIndex: number,
    pageSize: number,
    startDate: string,
    endDate: string,
  ) => {
    try {
      const topic = await get().getTopics(uuid);
      if (!topic) return [];

      const startTimeMs = new Date(startDate).getTime();
      const endTimeMs = new Date(endDate).getTime();

      const response = await telemetryApi.getDeviceDataTelemetry(
        uuid,
        topic,
        timestampFromMs(startTimeMs),
        timestampFromMs(endTimeMs),
        pageIndex,
        pageSize,
        AggregationType.AGGREGATION_UNSPECIFIED,
        BigInt(0),
      );

      if (!response.dataTelemetry || response.dataTelemetry.length === 0) return [];

      const values = response.dataTelemetry.filter((item) => item.data && item.timestamp);

      set((state) => {
        const entitiesPagination = { ...state.entitiesPagination };

        values.forEach((item) => {
          const ts = timestampMs(item.timestamp!);
          entitiesPagination[ts] = item;
        });

        return {
          entitiesPagination,
          totalEntitiesPagination: response.metadata?.totalCount,
        };
      });

      return values;
    } catch (error) {
      return [];
    }
  },
}));

export default useTelemetryStore;
